import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, ShieldCheck } from 'lucide-react';
import { Alert, Button, Field, Input, Logo } from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';
import { useAuthStore } from '../auth/auth.store';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);

  const [email, setEmail] = useState('admin@pnlglobal.com.br');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const session = await api.login({
        email,
        password,
        ...(twoFactorCode ? { twoFactorCode } : {}),
      });
      // Persist tokens
      const storage = (await import('@pnl/sdk')).localStorageTokenStorage('pnl_admin_');
      storage.setTokens(session.accessToken, session.refreshToken);
      // Hidrata store com permissions completas via /me
      const me = await api.me();
      setSession({
        id: me.user.id,
        email: me.user.email,
        fullName: me.user.fullName,
        roles: me.user.roles,
        permissions: me.user.permissions,
        requiresTwoFactor: me.user.twoFactorEnabled,
      });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof PnlApiError) {
        if (err.api.code === 'TWO_FACTOR_REQUIRED') {
          setNeedsTwoFactor(true);
          setError(null);
        } else if (err.api.code === 'INVALID_TWO_FACTOR') {
          setError('Código 2FA inválido. Confira no seu app autenticador.');
        } else if (err.api.code === 'INVALID_CREDENTIALS') {
          setError('Email ou senha inválidos.');
        } else if (err.api.code === 'EMAIL_NOT_VERIFIED') {
          setError('Verifique seu email antes de logar.');
        } else if (err.api.code === 'USER_BLOCKED') {
          setError('Conta bloqueada. Contate o suporte.');
        } else if (err.api.code === 'RATE_LIMITED') {
          setError('Muitas tentativas. Aguarde alguns minutos.');
        } else if (err.api.code === 'VALIDATION_ERROR') {
          setError('Verifique os dados informados.');
        } else {
          setError(err.api.message);
        }
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="animate-fade-in space-y-8">
      <div className="lg:hidden">
        <Logo />
      </div>

      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
          Acesso ao painel
        </p>
        <h2 className="font-display text-ink-900 mt-2 text-3xl font-semibold tracking-tight">
          Bem-vindo de volta
        </h2>
        <p className="text-ink-500 mt-1 text-sm">
          Use suas credenciais administrativas para entrar.
        </p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <form className="space-y-4" onSubmit={handleSubmit}>
        <Field label="Email" htmlFor="email" required>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            leadingIcon={<Mail size={14} />}
            placeholder="seu@email.com"
            disabled={submitting}
          />
        </Field>

        <Field label="Senha" htmlFor="senha" required>
          <Input
            id="senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            leadingIcon={<Lock size={14} />}
            placeholder="••••••••"
            disabled={submitting}
          />
        </Field>

        {needsTwoFactor && (
          <Field
            label="Código 2FA"
            htmlFor="2fa"
            required
            hint="6 dígitos do seu app autenticador (ou um código de recuperação)."
          >
            <Input
              id="2fa"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              autoComplete="one-time-code"
              required
              leadingIcon={<ShieldCheck size={14} />}
              placeholder="000 000"
              disabled={submitting}
            />
          </Field>
        )}

        <Button type="submit" block size="lg" loading={submitting}>
          {submitting ? 'Entrando…' : 'Entrar'}
        </Button>

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/forgot-password"
            className="text-brand-600 hover:text-brand-700 text-xs font-medium"
          >
            Esqueci minha senha
          </Link>
          <span className="text-2xs text-ink-400">2FA obrigatório p/ admins</span>
        </div>
      </form>
    </div>
  );
}
